import { Check } from "lucide-react";

import { cn } from "@/lib/utils";
import { palettes, PALETTE_KEYS, type PaletteKey } from "@/lib/palettes";

type Props = {
  value: PaletteKey | null;
  onChange: (key: PaletteKey | null) => void;
};

export function PalettePicker({ value, onChange }: Props) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {PALETTE_KEYS.map((key) => {
        const p = palettes[key];
        const selected = value === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(selected ? null : key)}
            className={cn(
              "group relative overflow-hidden rounded-2xl border bg-card text-left transition-all",
              selected
                ? "border-primary ring-2 ring-primary/30"
                : "border-border hover:border-primary/40",
            )}
          >
            {/* Mini app preview using the palette's own colors */}
            <div
              className="relative h-32 w-full"
              style={{ backgroundColor: p.bg }}
            >
              {/* Hero banner — primary→accent gradient */}
              <div
                className="absolute left-4 right-4 top-3 rounded-lg p-3"
                style={{
                  background: `linear-gradient(135deg, ${p.primary}, ${p.accent})`,
                  color: p.primaryInk,
                }}
              >
                <div
                  className="text-[8px] font-bold tracking-widest"
                  style={{ opacity: 0.85 }}
                >
                  OPŁACONE
                </div>
                <div
                  className="font-serif text-base leading-none"
                  style={{ fontFamily: "Georgia, serif" }}
                >
                  255 zł
                </div>
              </div>

              {/* Surface card with pill button — shows surface + primary CTA */}
              <div
                className="absolute bottom-3 left-4 right-4 rounded-md px-3 py-2"
                style={{
                  backgroundColor: p.surface,
                  borderWidth: 1,
                  borderColor: p.line,
                }}
              >
                <div className="flex items-center gap-2">
                  <div
                    className="font-serif text-xs"
                    style={{
                      color: p.ink,
                      fontFamily: "Georgia, serif",
                      flex: 1,
                    }}
                  >
                    Zarezerwuj
                  </div>
                  <div
                    className="rounded-full px-2 py-1 text-[9px] font-bold"
                    style={{
                      backgroundColor: p.primary,
                      color: p.primaryInk,
                    }}
                  >
                    UMÓW
                  </div>
                </div>
              </div>
            </div>

            {/* Swatches row */}
            <div
              className="flex items-center gap-1 px-3 py-2"
              style={{ borderTopWidth: 1, borderTopColor: "rgb(229 231 235)" }}
            >
              {[p.primary, p.primaryDark, p.accent, p.secondary, p.bg].map(
                (c, i) => (
                  <div
                    key={i}
                    className="h-4 w-4 rounded-full border border-black/5"
                    style={{ backgroundColor: c }}
                  />
                ),
              )}
            </div>

            {/* Name + description */}
            <div className="space-y-1 px-3 pb-3 pt-1">
              <div className="flex items-center gap-2">
                <div className="text-sm font-semibold text-foreground">
                  {p.name}
                </div>
                {p.isDark ? (
                  <span className="rounded-full bg-foreground/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                    Dark
                  </span>
                ) : null}
              </div>
              <p className="text-xs leading-snug text-muted-foreground">
                {p.desc}
              </p>
            </div>

            {/* Selected check badge */}
            {selected ? (
              <div className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md">
                <Check className="h-4 w-4" />
              </div>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
